import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Who can see what is decided server-side (App\Http\Controllers\Api\CalendarController):
//   'all'  administrators, and staff with "Inspections" + "Inspections - Scheduler"
//   'own'  inspectors — only jobs assigned to them
export type CalendarScope = 'all' | 'own';

export interface CalendarAccess {
    scope: CalendarScope;
    inspectors: { id: number, name: string }[];   // only filled for 'all'
}

export interface CalendarEvent {
    job_id: number;
    report_number: string;
    date: string;           // YYYY-MM-DD in the app timezone
    start: string;          // HH:mm
    end: string;            // HH:mm
    time_label: string;     // "8:30 AM" or "Anytime"
    anytime: boolean;
    status: 'Scheduled' | 'Completed' | 'Cancelled' | 'Hold';
    kind: 'inspection' | 'work';
    reinspection: boolean;
    type: string;
    address: string;
    city: string;
    client: string;
    agent_id: number;
    inspector: string;
}

// A time the inspector is busy on their own Google / Office 365 calendar. Times only: the
// server never reads event titles or details. Dates and times in the app timezone.
export interface BusyBlock {
    agent_id: number;
    date: string;           // YYYY-MM-DD
    start: string;          // HH:mm
    end: string;            // HH:mm, "24:00" when it runs to midnight
    all_day: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CalendarService {
    constructor(private http: HttpClient) { }

    access(): Observable<CalendarAccess> {
        return this.http.get<CalendarAccess>(environment.api + 'calendar/access');
    }

    // start / end are YYYY-MM-DD, inclusive. agentId narrows to one inspector ('all' scope only;
    // the server ignores it for inspectors).
    events(start: string, end: string, agentId?: number): Observable<{ events: CalendarEvent[] }> {
        let params = new HttpParams().set('start', start).set('end', end);
        if (agentId) {
            params = params.set('agent_id', String(agentId));
        }
        return this.http.get<{ events: CalendarEvent[] }>(environment.api + 'calendar/events', { params });
    }

    // Busy blocks (cached server-side for about two minutes). inspectorIds narrows the inspectors
    // ('all' scope only); leave it out for every inspector. unavailable: inspectors whose
    // calendar could not be read.
    busy(start: string, end: string, inspectorIds?: number[]): Observable<{ busy: BusyBlock[], unavailable: number[] }> {
        let params = new HttpParams().set('start', start).set('end', end);
        if (inspectorIds) {
            params = params.set('inspectors', inspectorIds.join(','));
        }
        return this.http.get<{ busy: BusyBlock[], unavailable: number[] }>(environment.api + 'calendar/busy', { params });
    }
}
